// generateMoveControlPdf.js
import jsPDF from "jspdf";
import logo from "../assets/icons/qvh-logo.svg";

import medialR from "../assets/footViews/medialR.png";
import medialL from "../assets/footViews/medialL.png";
import lateralR from "../assets/footViews/lateralR.png";
import lateralL from "../assets/footViews/lateralL.png";
import plantarR from "../assets/footViews/plantarR.png";
import plantarL from "../assets/footViews/plantarL.png";
import dorsalR from "../assets/footViews/dorsalR.png";
import dorsalL from "../assets/footViews/dorsalL.png";

// Normalize strings for correct rendering
function normalizeText(text) {
  return typeof text === "string" ? text.normalize("NFC") : text;
}

function clean(obj) {
  if (!obj) return obj;
  if (typeof obj === "string") return obj.normalize("NFC");
  if (Array.isArray(obj)) return obj.map((x) => clean(x));
  if (typeof obj === "object") {
    const out = {};
    for (const k in obj) out[k] = clean(obj[k]);
    return out;
  }
  return obj;
}

export async function generateMoveControlPdf(payloadRaw) {
  const payload = clean(payloadRaw);
  const {
    general_data: general,
    clinical_findings: clinical,
    foot_status: status,
    foot_status_graphic: graphic,
    criteria,
    categorization,
    shoe_provision: shoes,
  } = payload;

  const fussAuswahl = general?.fussAuswahl;
  const showLeftFoot = fussAuswahl === "rechts" ? false : true;
  const showRightFoot = fussAuswahl === "links" ? false : true;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;

  const col1Width = 92;
  const col2Width = 38;
  const col3Width = 38;
  const tableLeft = margin + 5;
  const col2Left = tableLeft + col1Width;
  const col3Left = col2Left + col2Width;

  const halfWidth = (pageWidth - margin * 2 - 10) / 2;
  const leftColStart = margin;
  const rightColStart = margin + halfWidth + 10;

  let y = 15;
  const today = new Date().toLocaleDateString("de-DE");
  let currentPage = 1;

  const logoPng = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 414; canvas.height = 177;
      canvas.getContext("2d").drawImage(img, 0, 0, 414, 177);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = logo;
  });

  const addHeader = () => {
    y = 15;
    const imgWidth = 40;
    const imgHeight = (177 / 414) * imgWidth;
    const imgX = (pageWidth - imgWidth) / 2;
    doc.addImage(logoPng, "PNG", imgX, y, imgWidth, imgHeight);
    doc.setFontSize(8);
    doc.text(`Datum: ${today}`, pageWidth - margin, y + 4, { align: "right" });
    y += imgHeight + 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Move control diab (Diabetes Befundbogen)", pageWidth / 2, y, { align: "center" });
    doc.setFontSize(9);
    y += 8;
  };

  const addFooter = (isLastPage = false) => {
    doc.setFontSize(7);
    doc.setTextColor(80);
    // Page number on EVERY page
    doc.text(
      `Seite ${currentPage}`,
      pageWidth - margin,
      pageHeight - 10,
      { align: "right" }
    );
    // Hint text ONLY on the last page
    if (isLastPage) {
      doc.text(
        "Hinweis: Diese Dokumentation dient der Kommunikation und ersetzt nicht die ärztliche Beurteilung.",
        margin,
        pageHeight - 10
      );
    }
  };

  addHeader();

  const sectionTitle = (text) => {
    if (y + 14 > pageHeight - 40) {
      addFooter(false);
      doc.addPage();
      currentPage++;
      y = 15;
    }
    doc.setTextColor(0);
    doc.setFillColor(235, 235, 235);
    doc.rect(margin - 2, y - 5, pageWidth - margin * 2 + 4, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(normalizeText(text), margin, y + 1);
    doc.setFontSize(9);
    y += 8;
  };

  // Allgemeine Daten
  sectionTitle("Allgemeine Daten");
  const pat = (label, value) => {
    if (value == null || value === "" || value === "—" || value === "/") return;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`${label}:`, margin, y);
    doc.setFont("helvetica", "normal");
    const txt = normalizeText(String(value));
    const maxWidth = pageWidth - margin * 2 - 45;
    const lines = doc.splitTextToSize(txt, maxWidth);
    doc.text(lines, margin + 55, y);
    y += lines.length * 4.8 + 0.5;
  };

  pat("Vorname", general?.vorname || "—");
  pat("Name", general?.name || "—");
  // pat("Patientencode", general?.barcode || payload.patient_encode || "—");
  pat("Geburtsdatum", general?.geburtsdatum || "—");
  pat("Geschlecht", general?.geschlecht || "—");
  pat("Größe", general?.groeße ? `${general.groeße} cm` : "—");
  pat("Gewicht", general?.gewicht ? `${general.gewicht} kg` : "—");
  pat("BMI", general?.bmi || "—");
  pat("Diabetes Typ", general?.diabetestyp || "—");
  pat("Dauer des Diabetes", general?.dauerDiab ? `${general.dauerDiab} Jahre` : "—");
  pat("MRSA", general?.mrsa || "—");
  pat("Teilnahme an Diab.-Schulung", general?.diabSchulung || "—");
  pat("Datum der Untersuchung", general?.datum || "—");
  y += 2;

// Klinischer Befund
sectionTitle("Klinischer Befund");
doc.setFont("helvetica", "bold");
doc.setFontSize(10);
if (showLeftFoot) {
  doc.text("Linker Fuß", col2Left + col2Width / 2, y, { align: "center" });
} else if (fussAuswahl === "rechts") {
  doc.text("Rechter Fuß", col2Left + col2Width / 2, y, { align: "center" });
}
if (showRightFoot && fussAuswahl !== "rechts") {
  doc.text("Rechter Fuß", col3Left + col3Width / 2, y, { align: "center" });
}
y += 4;
doc.setFontSize(9);
doc.setFont("helvetica", "normal");

const drawRow = (label, leftVal, rightVal) => {
  if ((!leftVal && !rightVal) || (String(leftVal).trim() === "" && String(rightVal).trim() === "")) return;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const labelText = normalizeText(label);
  const labelMaxWidth = col1Width - 14;
  const labelLines = doc.splitTextToSize(labelText, labelMaxWidth);
  doc.text(labelLines, margin, y + 3.5);
  doc.setFont("helvetica", "normal");
  const leftText = leftVal ? normalizeText(String(leftVal)) : "—";
  const rightText = rightVal ? normalizeText(String(rightVal)) : "—";
  if (showLeftFoot) {
    doc.text(leftText, col2Left + col2Width / 2, y + 3.5, { align: "center" });
  } else if (fussAuswahl === "rechts") {
    // rightFoot value in the leftFoot column when only right foot exists
    doc.text(rightText, col2Left + col2Width / 2, y + 3.5, { align: "center" });
  }
  if (showRightFoot && fussAuswahl !== "rechts") {
    doc.text(rightText, col3Left + col3Width / 2, y + 3.5, { align: "center" });
  }
  const rowHeight = Math.max(labelLines.length * 4, 7.5);
  y += rowHeight;
};

const drawRowLeftOnly = (label, leftVal) => {
  if (!leftVal || String(leftVal).trim() === "") return;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);

  const labelText = normalizeText(label);
  const labelMaxWidth = col1Width - 14;
  const labelLines = doc.splitTextToSize(labelText, labelMaxWidth);
  doc.text(labelLines, margin, y + 3.5);

  doc.setFont("helvetica", "normal");

  const leftText = normalizeText(String(leftVal));

  // Draw only in left‑foot column (or right‑foot when only right is selected)
  if (fussAuswahl === "rechts") {
    doc.text(leftText, col2Left + col2Width / 2, y + 3.5, { align: "center" });
  } else {
    doc.text(leftText, col2Left + col2Width / 2, y + 3.5, { align: "center" });
  }

  const rowHeight = Math.max(labelLines.length * 4, 7.5);
  y += rowHeight + 2;
};

// Helper for Zehenamputation - shows which toes were amputated (sorted)
const getZehenAmputationText = (jaNein, toesArray) => {
  if (jaNein !== "ja") return jaNein || "—";
  
  if (!toesArray || toesArray.length === 0) {
    return "ja (keine Zehen ausgewählt)";
  }
  
  // Sort toes in ascending order: D1, D2, D3, D4, D5
  const sortedToes = [...toesArray].sort((a, b) => {
    const numA = parseInt(a.replace("D", ""), 10);
    const numB = parseInt(b.replace("D", ""), 10);
    return numA - numB;
  });

  return `ja (${sortedToes.join(", ")})`;
};



  drawRow("Mikrofilament-Erkennung (am Met.-köpfchen 2 plantar)", clinical?.linksMikrofilament, clinical?.rechtsMikrofilament);
  drawRow("Vibrationsempfindung (am D1-Grundgelenk)", clinical?.linksVibrationsempfindung ? clinical.linksVibrationsempfindung + "/8" : "—", clinical?.rechtsVibrationsempfindung ? clinical.rechtsVibrationsempfindung + "/8" : "—");
  drawRow("Hinweis PNP (Sensibilitätsverlust)", clinical?.linksHinweisPNP, clinical?.rechtsHinweisPNP);
  drawRow("Hinweis PAVK (periphere arterielle Verschlusskrankheit)", clinical?.linksHinweisPAVK, clinical?.rechtsHinweisPAVK);
  y += 6;

  // Fußstatus
  sectionTitle("Fußstatus");

    if (status?.dialysepflichtigeNiereninsuffienz != null) {
    drawRowLeftOnly("Dialysepflichtige Niereninsuffizienz", 
        status.dialysepflichtigeNiereninsuffienz, 
        null);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
if (showLeftFoot) {
  doc.text("Linker Fuß", col2Left + col2Width / 2, y, { align: "center" });
} else if (fussAuswahl === "rechts") {
  doc.text("Rechter Fuß", col2Left + col2Width / 2, y, { align: "center" });
}
if (showRightFoot && fussAuswahl !== "rechts") {
  doc.text("Rechter Fuß", col3Left + col3Width / 2, y, { align: "center" });
}
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");



  drawRow("Drohende dorsale Ulcera (Schuhe RG IIa nicht passend)", status?.linksDrohendeDorsaleUlcera, status?.rechtsDrohendeDorsaleUlcera);
  drawRow("Präulzerative Veränderungen", status?.linksPraeulzerativeVeranderungen, status?.rechtsPraeulzerativeVeranderungen);
  drawRow("Ulcus", status?.linksUlcus + (status?.linksUlcus === "ja" && status?.linksUlcusTyp ? ` (${status.linksUlcusTyp})` : ""), status?.rechtsUlcus + (status?.rechtsUlcus === "ja" && status?.rechtsUlcusTyp ? ` (${status.rechtsUlcusTyp})` : ""));
    //  Zustand nach Zehenamputation with specific toes
  const leftZehenText = getZehenAmputationText(
    status?.linksZustandNachZehenamputation, 
    status?.linksZehenamputation
  );
  
  const rightZehenText = getZehenAmputationText(
    status?.rechtsZustandNachZehenamputation, 
    status?.rechtsZehenamputation
  );

  drawRow("Zustand nach Zehenamputation", leftZehenText, rightZehenText);
  drawRow("Fußdeformitäten / Dysproportionen", status?.linksFussdeformitaeten, status?.rechtsFussdeformitaeten);
  drawRow("Dorsalextension OSG eingeschränkt", status?.linksDorsalextensionOSG, status?.rechtsDorsalextensionOSG);
  drawRow("Extension Großzehengrundgelenk eingeschränkt", status?.linksExtensionGrosszehengrundgelenk, status?.rechtsExtensionGrosszehengrundgelenk);
  drawRow("Hinweis auf CNO", status?.linksHinweisCNO + (status?.linksHinweisCNO === "ja" && status?.linksHinweisCNOTyp ? ` (${status.linksHinweisCNOTyp})` : ""), status?.rechtsHinweisCNO + (status?.rechtsHinweisCNO === "ja" && status?.rechtsHinweisCNOTyp ? ` (${status.rechtsHinweisCNOTyp})` : ""));
  drawRow("Zustand nach (mind. transmetatarsaler) Amputation", status?.linksZustandNachAmputation, status?.rechtsZustandNachAmputation);
  drawRow("Pedographischer Befund (Druckwert)", status?.linksPedographischerBefund, status?.rechtsPedographischerBefund);
  y += 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Orthopädische / neurologische Zusatzbefunde, Gangabweichung:", tableLeft + 4, y + 3.5);
  y += 4;

  if (status?.orthoNeurologischeZusatzbefunde?.length > 0) {
    doc.setFont("helvetica", "normal");
    const orthoText = status.orthoNeurologischeZusatzbefunde.join(", ");
    const orthoLines = doc.splitTextToSize(orthoText, pageWidth - margin * 2 - 16);
    doc.text(orthoLines, tableLeft + 4, y + 3.5);
    y += orthoLines.length * 5.2 + 4;
    y += 1;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(40, 100, 180);
    const advText = `Sie sollten aufgrund Ihrer Auswahl (${orthoText}) Ihre Auswahl erweitern oder Rücksprache mit einer Ärztin / einem Arzt halten.`;
    const advLines = doc.splitTextToSize(advText, pageWidth - margin * 2 - 30);
    doc.text(advLines, margin + 4, y + 3.5);
    y += advLines.length * 5.2 + 3;
    doc.setTextColor(0);
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("Keine Auswahl getroffen.", tableLeft + 4, y + 3.5);
    y += 7;
  }

  // Kriterien
  sectionTitle("Kriterien für eine höhergradige Versorgung");

  y -= 3;

  const kriterienList = [
    { field: "orthopaedischeZusatzkomplikation", label: "Orthopädische Zusatzkomplikation" },
    { field: "neurologischeZusatzkomplikation", label: "Neurologische Zusatzkomplikation (motorische Funktionseinschränkung / Parese eines oder beider Beine)" },
    { field: "fehlgeschlageneVorversorgung", label: "Fehlgeschlagene adäquate Vorversorgung" },
  ];
  const selectedKriterien = kriterienList.filter(k => criteria?.[k.field] === "ja");

  if (selectedKriterien.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("Keine Kriterien ausgewählt.", margin, y + 3.5);
    y += 9;
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    selectedKriterien.forEach((k) => {
      doc.text(`• ${k.label}`, margin, y + 3.5);
      y += 5.5;
    });
    y += 6;
  }

  if (selectedKriterien.length > 0) {
    y -= 3;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(40, 100, 180);
    const selectedLabels = selectedKriterien.map(k => k.label).join(", ");
    const advText = `Sie sollten aufgrund Ihrer Auswahl (${selectedLabels}) Ihre Auswahl erweitern oder Rücksprache mit einer Ärztin / einem Arzt halten.`;
    const advLines = doc.splitTextToSize(advText, pageWidth - margin * 2 - 30);
    doc.text(advLines, margin + 4, y + 3.5);
    y += advLines.length * 5.2 + 7;
    doc.setTextColor(0);
  }

  // Kategorisierung nach Risikogruppen
  sectionTitle("Kategorisierung nach Risikogruppen");
  const leftType = categorization?.linksRisikotyp || "Kein Typ";
  const rightType = categorization?.rechtsRisikotyp || "Kein Typ";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  if (showLeftFoot) {
    doc.text("Linker Fuß", leftColStart, y + 3.5);
  } else if (fussAuswahl === "rechts") {
    doc.text("Rechter Fuß", leftColStart, y + 3.5);
  }
  if (showRightFoot && fussAuswahl !== "rechts") {
    doc.text("Rechter Fuß", rightColStart, y + 3.5);
  }
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (showLeftFoot) {
    doc.text(`Risikogruppe: ${leftType}`, leftColStart, y + 3.5);
  } else if (fussAuswahl === "rechts") {
    doc.text(`Risikogruppe: ${rightType}`, leftColStart, y + 3.5);
  }
  if (showRightFoot && fussAuswahl !== "rechts") {
    doc.text(`Risikogruppe: ${rightType}`, rightColStart, y + 3.5);
  }
  y += 14;

  // Schuhversorgung
  sectionTitle("Schuhversorgung (technische Auslegung) gemäß Risikogruppe");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  if (showLeftFoot) {
    doc.text("Linker Fuß", leftColStart, y + 3.5);
  } else if (fussAuswahl === "rechts") {
    doc.text("Rechter Fuß", leftColStart, y + 3.5);
  }
  if (showRightFoot && fussAuswahl !== "rechts") {
    doc.text("Rechter Fuß", rightColStart, y + 3.5);
  }
  y += 8;

  const rowConfigs = [
    { typ: "0", beschreibung: "Fußgerechte Konfektionsschuhe", leftKey: "konfekt", rightKey: "konfekt", text: "Fußgerechte Konfektionsschuhe" },
    { typ: "I", beschreibung: "Orthopädieschuhtechnische Versorgung", leftKeys: ["einlage", "zurichtung", "massschuh"], rightKeys: ["einlage", "zurichtung", "massschuh"], texts: ["mit orthopädischer Einlage", "mit Schuhzurichtung", "mit Maßschuh"] },
    { typ: "IIa", beschreibung: "Für DFS geeignete Schuhe", leftKeys: ["weichsohle", "weichbettung"], rightKeys: ["weichsohle", "weichbettung"], texts: ["mit herausnehmbarer Weichpolstersohle", "mit Weichpolsterbettungseinlage (elastisch, druckumverteilend, nach Maß)"] },
    { typ: "IIb", beschreibung: "Spezialschuhe bei DFS mit DAF", leftKey: "daf_iib", rightKey: "daf_iib", text: "mit diabetesadaptierter Fußbettung (DAF)" },
    { typ: "III", beschreibung: "Spezialschuhe bei DFS mit DAF", leftKeys: ["daf_iii", "ortho_zurichtung"], rightKeys: ["daf_iii", "ortho_zurichtung"], texts: ["mit diabetesadaptierter Fußbettung (DAF)", "mit orthopädischer Schuhzurichtung"] },
    { typ: "IV", beschreibung: "Orthopädische Maßschuhe mit DAF", leftKey: "daf_iv", rightKey: "daf_iv", text: "mit diabetesadaptierter Fußbettung (DAF)" },
    { typ: "V", beschreibung: "Knöchelübergreifende orthopädische Maßschuhe mit DAF", leftKeys: ["usg", "osg", "ruhig_wade"], rightKeys: ["usg", "osg", "ruhig_wade"], texts: ["USG-stabilisierende Maßnahmen", "OSG-stabilisierende Maßnahmen", "Komplette Ruhigstellung mit Innenschuh über Wadenbauch"] },
    { typ: "VI", beschreibung: "(Knöchelübergreifende) orthopädische Maßschuhe mit DAF", leftKeys: ["amputation", "prothese"], rightKeys: ["amputation", "prothese"], texts: ["Knöchelübergreifende Amputationsversorgung", "Prothese, ggf. bis zum Knie"] },
    { typ: "VIIa", beschreibung: "Verbandsschuhe", leftKey: "verband", rightKey: "verband", text: "Verbandsschuhe" },
    { typ: "VIIb", beschreibung: "Individuelle kniehohe Orthese mit DAF", leftKeys: ["nicht_abnehmbar", "abnehmbar"], rightKeys: ["nicht_abnehmbar", "abnehmbar"], texts: ["nicht abnehmbar", "abnehmbar"] },
    { typ: "VIIc", beschreibung: "Kniehohe Orthese", leftKeys: ["konfektioniert_nicht_abnehmbar", "konfektioniert_abnehmbar", "individuell_nicht_abnehmbar", "individuell_abnehmbar", "interimsschuh"], rightKeys: ["konfektioniert_nicht_abnehmbar", "konfektioniert_abnehmbar", "individuell_nicht_abnehmbar", "individuell_abnehmbar", "interimsschuh"], texts: ["konfektioniert - nicht abnehmbar", "konfektioniert - abnehmbar", "individuell - nicht abnehmbar", "individuell - abnehmbar", "Interimsschuh"] },
    { typ: "VIId", beschreibung: "Kniehohe individuelle Fersenentlastungsorthese", leftKeys: ["nicht_abnehmbar_ferse", "abnehmbar_ferse"], rightKeys: ["nicht_abnehmbar_ferse", "abnehmbar_ferse"], texts: ["nicht abnehmbar", "abnehmbar"] },
  ];

  const leftItems = [];
  const rightItems = [];

  rowConfigs.forEach(config => {
    let leftSelected = Array.isArray(config.leftKeys)
      ? config.leftKeys.filter(k => shoes?.left?.[k] === "ja")
      : (shoes?.left?.[config.leftKey] === "ja" ? [config.leftKey] : []);
    if (leftSelected.length > 0) {
      const auswahlText = leftSelected.map(k => {
        const index = config.leftKeys ? config.leftKeys.indexOf(k) : 0;
        return config.texts?.[index] || k.replace(/_/g, " ");
      }).join(", ");
      leftItems.push({
        typ: config.typ,
        beschreibung: config.beschreibung,
        auswahl: auswahlText
      });
    }

    let rightSelected = Array.isArray(config.rightKeys)
      ? config.rightKeys.filter(k => shoes?.right?.[k] === "ja")
      : (shoes?.right?.[config.rightKey] === "ja" ? [config.rightKey] : []);
    if (rightSelected.length > 0) {
      const auswahlText = rightSelected.map(k => {
        const index = config.rightKeys ? config.rightKeys.indexOf(k) : 0;
        return config.texts?.[index] || k.replace(/_/g, " ");
      }).join(", ");
      rightItems.push({
        typ: config.typ,
        beschreibung: config.beschreibung,
        auswahl: auswahlText
      });
    }
  });

  const maxItems = Math.max(leftItems.length, rightItems.length);
  for (let i = 0; i < maxItems; i++) {
    const left = leftItems[i] || null;
    const right = rightItems[i] || null;
    if (y + 32 > pageHeight - 40) {
      addFooter(false);
      doc.addPage();
      currentPage++;
      y = 15;
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      if (showLeftFoot) {
        doc.text("Linker Fuß", leftColStart, y + 3.5);
      } else if (fussAuswahl === "rechts") {
        doc.text("Rechter Fuß", leftColStart, y + 3.5);
      }
      if (showRightFoot && fussAuswahl !== "rechts") {
        doc.text("Rechter Fuß", rightColStart, y + 3.5);
      }
      y += 7;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (showLeftFoot && left) {
      doc.text(`Risikogruppe: Typ ${left.typ}`, leftColStart, y + 3.5);
    } else if (fussAuswahl === "rechts" && right) {
      doc.text(`Risikogruppe: Typ ${right.typ}`, leftColStart, y + 3.5);
    }
    if (showRightFoot && right && fussAuswahl !== "rechts") {
      doc.text(`Risikogruppe: Typ ${right.typ}`, rightColStart, y + 3.5);
    }
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (showLeftFoot) {
      doc.text("Beschreibung:", leftColStart, y + 3.5);
    } else if (fussAuswahl === "rechts") {
      doc.text("Beschreibung:", leftColStart, y + 3.5);
    }
    if (showRightFoot && fussAuswahl !== "rechts") {
      doc.text("Beschreibung:", rightColStart, y + 3.5);
    }
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const leftBeschreibung = left ? left.beschreibung : "";
    const rightBeschreibung = right ? right.beschreibung : "";
    const leftBeschreibungLines =
      doc.splitTextToSize(
        leftBeschreibung,
        halfWidth - 16
      );
    const rightBeschreibungLines =
      doc.splitTextToSize(
        rightBeschreibung,
        halfWidth - 16
      );
    if (showLeftFoot) {
      doc.text(leftBeschreibungLines, leftColStart, y + 3.5);
    } else if (fussAuswahl === "rechts") {
      doc.text(rightBeschreibungLines, leftColStart, y + 3.5);
    }
    if (showRightFoot && fussAuswahl !== "rechts") {
      doc.text(rightBeschreibungLines, rightColStart, y + 3.5);
    }
    y += Math.max(leftBeschreibungLines.length, rightBeschreibungLines.length) * 5 + 3;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (showLeftFoot) {
      doc.text("Auswahl:", leftColStart, y + 3.5);
    } else if (fussAuswahl === "rechts") {
      doc.text("Auswahl:", leftColStart, y + 3.5);
    }
    if (showRightFoot && fussAuswahl !== "rechts") {
      doc.text("Auswahl:", rightColStart, y + 3.5);
    }
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const leftAuswahl = left ? left.auswahl : "";
    const rightAuswahl = right ? right.auswahl : "";
    const leftLines =
      doc.splitTextToSize(leftAuswahl, halfWidth - 16);
    const rightLines =
      doc.splitTextToSize(rightAuswahl, halfWidth - 16);
    if (showLeftFoot) {
      doc.text(leftLines, leftColStart, y + 3.5);
    } else if (fussAuswahl === "rechts") {
      doc.text(rightLines, leftColStart, y + 3.5);
    }
    if (showRightFoot && fussAuswahl !== "rechts") {
      doc.text(rightLines, rightColStart, y + 3.5);
    }
    y += Math.max(leftLines.length, rightLines.length) * 5 + 8;
  }

  if (leftItems.length === 0 && rightItems.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("Keine Schuhversorgung ausgewählt.", leftColStart, y + 3.5);
    y += 9;
  }

// ==================== Fußstatus Grafik (Befunde) ====================
  sectionTitle("Fußstatus Grafik (Befunde)");

    const getFootImage = (viewTitle, side) => {
    const view = viewTitle.toLowerCase();

    if (view === "medial") return side === "left" ? medialL : medialR;
    if (view === "lateral") return side === "left" ? lateralL : lateralR;
    if (view === "plantar") return side === "left" ? plantarL : plantarR;
    if (view === "dorsal") return side === "left" ? dorsalL : dorsalR;

    return null;
  };

  const drawViewColumns = (viewTitle, leftFindings = [], rightFindings = []) => {
    if (leftFindings.length === 0 && rightFindings.length === 0) return;

    if (y + 24 > pageHeight - 40) {
      addFooter(false);
      doc.addPage();
      currentPage++;
      y = 15;
      sectionTitle("Fußstatus Grafik (Befunde)");
      y += 6;
    }

    y += 1;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);

    const isOnlyRightFoot = fussAuswahl === "rechts";
    const titleX = isOnlyRightFoot ? leftColStart : margin;
    const footLabelX = isOnlyRightFoot ? leftColStart + 4 : rightColStart + 4;
    const findingsX = isOnlyRightFoot ? leftColStart + 4 : rightColStart + 4;

    doc.text(viewTitle, titleX, y);
    y += 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    if (isOnlyRightFoot) {
      doc.text("Rechter Fuß", footLabelX, y + 3.5);
    } else {
      if (showLeftFoot) doc.text("Linker Fuß", leftColStart + 4, y + 3.5);
      if (showRightFoot) doc.text("Rechter Fuß", rightColStart + 4, y + 3.5);
    }
    y += 4;

    const maxItems = Math.max(leftFindings.length, rightFindings.length);

    for (let i = 0; i < maxItems; i++) {
      if (y + 9 > pageHeight - 40) {
        addFooter(false);
        doc.addPage();
        currentPage++;
        y = 15;
        sectionTitle("Fußstatus Grafik (Befunde)");
        y += 6;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(viewTitle, titleX, y);
        y += 3;
        doc.setFontSize(9);
        if (isOnlyRightFoot) {
          doc.text("Rechter Fuß", footLabelX, y + 3.5);
        } else {
          if (showLeftFoot) doc.text("Linker Fuß", leftColStart + 4, y + 3.5);
          if (showRightFoot) doc.text("Rechter Fuß", rightColStart + 4, y + 3.5);
        }
        y += 5;
      }

      const left = leftFindings[i] || null;
      const right = rightFindings[i] || null;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      if (isOnlyRightFoot && right) {
        doc.text(`${right.loc || "—"}: ${right.befund || "—"}`, findingsX, y + 3.5);
      } else {
        if (showLeftFoot && left) {
          doc.text(`${left.loc || "—"}: ${left.befund || "—"}`, leftColStart + 4, y + 3.5);
        }
        if (showRightFoot && right) {
          doc.text(`${right.loc || "—"}: ${right.befund || "—"}`, rightColStart + 4, y + 3.5);
        }
      }
      y += 4.8;
    }
    y += 6; // extra spacing between views

    // ========== ADD IMAGE BELOW FINDINGS WITH EXACT DIMENSIONS ==========
    const hasLeftFindings = leftFindings.length > 0;
    const hasRightFindings = rightFindings.length > 0;

    if (hasLeftFindings || hasRightFindings) {
      const view = viewTitle.toLowerCase();
      let imgWidth;
      let imgHeight;

      if (view === "medial") {
        imgWidth = 53;
        imgHeight = 28;
      } else if (view === "lateral") {
        imgWidth = 53;
        imgHeight = 29;
      } else if (view === "plantar") {
        imgWidth = 32;
        imgHeight = 66;
      } else if (view === "dorsal") {
        imgWidth = 32;
        imgHeight = 70;
      } else {
        imgWidth = 34;
        imgHeight = 34;
      }

      const requiredSpace = imgHeight + 6;

      // Check if we need a new page for the image
      if (y + requiredSpace > pageHeight - 40) {
        addFooter(false);
        doc.addPage();
        currentPage++;
        y = 15;
        // Re-add section title on new page
        sectionTitle("Fußstatus Grafik (Befunde)");
        y += 6;
      }

      if (isOnlyRightFoot) {
        const img = getFootImage(viewTitle, "right");
        if (img) {
          doc.addImage(img, "PNG", leftColStart + 18, y, imgWidth, imgHeight);
          y += imgHeight + 6;
        }
      } else {
        if (hasLeftFindings) {
          const imgLeft = getFootImage(viewTitle, "left");
          if (imgLeft) {
            doc.addImage(imgLeft, "PNG", leftColStart + 18, y, imgWidth, imgHeight);
          }
        }

        if (hasRightFindings) {
          const imgRight = getFootImage(viewTitle, "right");
          if (imgRight) {
            doc.addImage(imgRight, "PNG", rightColStart + 18, y, imgWidth, imgHeight);
          }
        }

        y += imgHeight + 3;
      }
    }
  };

    // Render the views
  if (fussAuswahl === "rechts") {
    drawViewColumns("Medial",  [], graphic?.medRight || []);
    drawViewColumns("Lateral", [], graphic?.latRight || []);
    drawViewColumns("Plantar", [], graphic?.plRight || []);
    drawViewColumns("Dorsal",  [], graphic?.dorsRight || []);
  } else {
    drawViewColumns("Medial",  graphic?.medLeft || [], graphic?.medRight || []);
    drawViewColumns("Lateral", graphic?.latLeft || [], graphic?.latRight || []);
    drawViewColumns("Plantar", graphic?.plLeft || [], graphic?.plRight || []);
    drawViewColumns("Dorsal",  graphic?.dorsLeft || [], graphic?.dorsRight || []);
  }


  // ==================== NOTIZEN SECTION ====================
  const ulcusNotiz = (status?.UlcusNotiz || "").trim();
  const kriterienNotiz = (criteria?.KriterienNotiz || "").trim();

  // Only show "Notizen" section if at least one note exists
  if (ulcusNotiz.length > 0 || kriterienNotiz.length > 0) {
    y += 5;

    if (y > pageHeight - 45) {
      addFooter(false);
      doc.addPage();
      currentPage += 1;
      y = 15;
    }

    sectionTitle("Notizen");

    // Ulcus Notiz
    if (ulcusNotiz.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Ulcus – Notiz", margin, y);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const ulcusLines = doc.splitTextToSize(normalizeText(ulcusNotiz), pageWidth - margin * 2);
      doc.text(ulcusLines, margin, y);
      y += ulcusLines.length * 4.8 + (kriterienNotiz.length > 0 ? 2 : 8);
    }

    // Kriterien Notiz - appears directly below Ulcus
    if (kriterienNotiz.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Kriterien – Notiz", margin, y);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const kriterienLines = doc.splitTextToSize(normalizeText(kriterienNotiz), pageWidth - margin * 2);
      doc.text(kriterienLines, margin, y);
      y += kriterienLines.length * 4.8 + 2;
    }
  }

  y += 8;
  addFooter(true);

  // Universal cross-browser download using Blob
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);

  // Dynamic Filename 
  const patientNachname = (general?.name || "").trim();
  const patientVorname = (general?.vorname || "").trim();
  const currentDate = new Date().toISOString().slice(0, 10);

  let fileName = "Move_control_Diab";
  if (patientNachname || patientVorname) {
    const nameParts = [];
    if (patientNachname) nameParts.push(patientNachname);
    if (patientVorname) nameParts.push(patientVorname);
    const fullName = nameParts.join("_");
    const safeName = fullName
      .normalize("NFC")
      .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, "_");
    fileName += `_${safeName}`;
  }
  fileName += `_${currentDate}.pdf`;

  const a = document.createElement("a");
  a.href = pdfUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Cleanup memory
  setTimeout(() => {
    URL.revokeObjectURL(pdfUrl);
  }, 1000);
}